import { createTheme } from "@mui/material/styles";
import { paperClasses } from "@mui/material/Paper";
import { Dayjs } from "dayjs";
import "@mui/material/styles";
import "@mui/x-date-pickers/themeAugmentation";
import { COMPACT_QUERY } from "./utils/hooks/useCompact";

// Compact density for ~720p customer machines (1280x650 down to 1024x600).
// Scoped behind COMPACT_QUERY so full-hd/2k/4k stay byte-identical.
const compact = `@media ${COMPACT_QUERY}`;

const THAI_WEEKDAY_ABBREVIATIONS = ["อา", "จ", "อ", "พ", "พฤ", "ศ", "ส"];

const formatThaiWeekday = (date: Dayjs) =>
  THAI_WEEKDAY_ABBREVIATIONS[date.day()] ?? "";

declare module "@mui/material/styles" {
  interface Palette {
    draft: Palette["primary"];
    gray: Palette["primary"];
    link: Palette["primary"];
  }
  interface PaletteOptions {
    draft?: PaletteOptions["primary"];
    gray?: PaletteOptions["primary"];
    link?: PaletteOptions["primary"];
  }
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#5C69E8",
      // main: "#fda42c",
    },
    error: {
      main: "#fb3b3b",
    },
    warning: {
      main: "#FDA42C",
    },
    success: {
      main: "#45be77",
    },
    draft: {
      main: "#d9d9d9",
    },
    gray: {
      main: "#6a6a6a",
    },
    link: {
      main: "#5c69e8",
    },
    background: {
      default: "#f3f3f6",
    },
  },
  typography: {
    fontFamily: "'Prompt', 'sans-serif'",
    fontSize: 16,
    // Headings shrink slightly on compact screens; body text stays for readability.
    h4: { [compact]: { fontSize: "1.6rem" } },
    h5: { [compact]: { fontSize: "1.25rem" } },
    h6: { [compact]: { fontSize: "1.05rem" } },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        contained: {
          color: "#fff",
          boxShadow: "none",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: "16px",
          [compact]: { padding: "12px" },
        },
      },
    },
    // A dropdown surface is a Paper too, so the card padding above framed every open
    // Select/Menu with a gap instead of letting it hug its items. Only the padding is
    // undone; the rounded corner still belongs on the menu. The selector is doubled up
    // to outrank the Paper rule no matter which class emotion inserts last.
    MuiMenu: {
      styleOverrides: {
        paper: {
          [`&.${paperClasses.root}`]: { padding: 0 },
        },
      },
    },
    // Denser table cells on compact so more rows fit on a 650px-tall screen.
    MuiTableCell: {
      styleOverrides: {
        root: {
          [compact]: { padding: "8px 10px" },
        },
      },
    },
    MuiDayCalendar: {
      defaultProps: {
        dayOfWeekFormatter: formatThaiWeekday,
      },
    },
  },
});

export default theme;
